<template>
  <div v-if="dailyPointBars.length" class="daily-points-card">
    <div class="daily-points-card__header">
      <div>
        <h4>Jakson päiväpisteet</h4>
        <p>Vie pylvään päälle tai napauta päivää nähdäksesi, mistä pisteet tulivat.</p>
      </div>
      <div v-if="bestDailyPointDay" class="daily-points-card__best">
        Paras päivä {{ formatDisplayDate(bestDailyPointDay.date) }} · {{ formatPoints(bestDailyPointDay.totalPoints) }} p
      </div>
    </div>

    <div class="daily-points-chart" role="group" aria-label="Jakson päiväpisteet päivittäin">
      <button
        v-for="day in dailyPointBars"
        :key="'daily-points-' + day.date"
        type="button"
        :class="['daily-points-bar-button', { active: selectedDailyPointDay && selectedDailyPointDay.date === day.date }]"
        :aria-label="formatDailyPointLabel(day)"
        @mouseenter="setActiveDailyPointDate(day.date)"
        @focus="setActiveDailyPointDate(day.date)"
        @click="setActiveDailyPointDate(day.date)"
      >
        <span class="daily-points-bar-track">
          <span
            class="daily-points-bar"
            :style="{ height: day.barHeight + '%' }"
          ></span>
        </span>
        <span class="daily-points-bar-value">{{ formatPoints(day.totalPoints) }}</span>
        <span class="daily-points-bar-date">{{ formatDisplayDate(day.date) }}</span>
      </button>
    </div>

    <div v-if="selectedDailyPointDay" class="daily-points-detail">
      <div class="daily-points-detail__summary">
        <strong>{{ formatDisplayDate(selectedDailyPointDay.date) }}</strong>
        <span>{{ formatPoints(selectedDailyPointDay.totalPoints) }} p</span>
        <small>
          Hevoset {{ formatPoints(selectedDailyPointDay.horsePoints) }} p ·
          Ohjastaja {{ formatPoints(selectedDailyPointDay.jockeyPoints) }} p
        </small>
      </div>

      <div class="daily-points-detail__groups">
        <div v-if="selectedDailyPointDay.horses.length" class="daily-points-detail__group">
          <h5>Hevoset</h5>
          <button
            v-for="(item, index) in selectedDailyPointDay.horses"
            :key="'daily-horse-' + selectedDailyPointDay.date + '-' + (item.selectionKey || item.name || index) + '-' + index"
            type="button"
            :class="['daily-points-detail__row', { clickable: canViewDetails(item) }]"
            @click="handleBreakdownClick(item)"
          >
            <span>{{ item.name }}</span>
            <strong>{{ formatPoints(item.points) }} p</strong>
          </button>
        </div>
        <div v-if="selectedDailyPointDay.jockeys.length" class="daily-points-detail__group">
          <h5>Ohjastaja</h5>
          <button
            v-for="(item, index) in selectedDailyPointDay.jockeys"
            :key="'daily-jockey-' + selectedDailyPointDay.date + '-' + (item.selectionKey || item.name || index) + '-' + index"
            type="button"
            :class="['daily-points-detail__row', { clickable: canViewDetails(item) }]"
            @click="handleBreakdownClick(item)"
          >
            <span>{{ item.name }}</span>
            <strong>{{ formatPoints(item.points) }} p</strong>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';

export default {
  name: 'DailyPointsChart',
  props: {
    pointsBreakdown: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['viewHorse', 'viewJockey'],
  setup(props, { emit }) {
    const activeDailyPointDate = ref(null);

    const toNumber = (value) => {
      const parsed = Number(value || 0);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const formatPoints = (value) => {
      const points = toNumber(value);
      return Number.isInteger(points) ? String(points) : points.toFixed(1);
    };

    const formatDisplayDate = (dateString) => {
      if (!dateString) {
        return '';
      }
      const parts = String(dateString).split('-');
      if (parts.length !== 3) {
        return dateString;
      }
      return `${Number(parts[2])}.${Number(parts[1])}.`;
    };

    const dailyPointDays = computed(() => {
      const breakdown = Array.isArray(props.pointsBreakdown) ? props.pointsBreakdown : [];
      const grouped = new Map();

      breakdown.forEach((item) => {
        if (!item?.date) {
          return;
        }

        if (!grouped.has(item.date)) {
          grouped.set(item.date, {
            date: item.date,
            totalPoints: 0,
            horsePoints: 0,
            jockeyPoints: 0,
            horses: [],
            jockeys: [],
          });
        }

        const day = grouped.get(item.date);
        const points = toNumber(item.points);
        const normalized = {
          ...item,
          points,
        };

        day.totalPoints += points;
        if (item.type === 'Horse') {
          day.horsePoints += points;
          day.horses.push(normalized);
        } else if (item.type === 'Jockey') {
          day.jockeyPoints += points;
          day.jockeys.push(normalized);
        }
      });

      return [...grouped.values()]
        .map((day) => ({
          ...day,
          horses: day.horses.sort((a, b) => toNumber(b.points) - toNumber(a.points) || String(a.name || '').localeCompare(String(b.name || ''))),
          jockeys: day.jockeys.sort((a, b) => toNumber(b.points) - toNumber(a.points) || String(a.name || '').localeCompare(String(b.name || ''))),
        }))
        .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
    });

    const bestDailyPointDay = computed(() => {
      return dailyPointDays.value.reduce((best, day) => {
        if (!best || toNumber(day.totalPoints) > toNumber(best.totalPoints)) {
          return day;
        }
        return best;
      }, null);
    });

    const selectedDailyPointDay = computed(() => {
      if (!dailyPointDays.value.length) {
        return null;
      }
      return dailyPointDays.value.find((day) => day.date === activeDailyPointDate.value) || bestDailyPointDay.value || dailyPointDays.value[0];
    });

    const dailyPointBars = computed(() => {
      const maxPoints = Math.max(...dailyPointDays.value.map((day) => toNumber(day.totalPoints)), 0);
      return dailyPointDays.value.map((day) => ({
        ...day,
        barHeight: maxPoints > 0 ? Math.max(8, Math.round((toNumber(day.totalPoints) / maxPoints) * 100)) : 0,
      }));
    });

    const setActiveDailyPointDate = (date) => {
      activeDailyPointDate.value = date;
    };

    const formatDailyPointLabel = (day) => {
      const horseText = day.horses.map((item) => `${item.name} ${formatPoints(item.points)} p`).join(', ');
      const jockeyText = day.jockeys.map((item) => `${item.name} ${formatPoints(item.points)} p`).join(', ');
      return [
        `${formatDisplayDate(day.date)} yhteensä ${formatPoints(day.totalPoints)} p`,
        horseText ? `Hevoset: ${horseText}` : '',
        jockeyText ? `Ohjastaja: ${jockeyText}` : '',
      ].filter(Boolean).join('. ');
    };

    const extractIdFromKey = (key) => {
      if (!key || typeof key !== 'string') {
        return null;
      }
      const parts = key.split('#');
      return parts.length > 0 ? parts[parts.length - 1] : null;
    };

    const canViewDetails = (item) => {
      return (item.type === 'Horse' || item.type === 'Jockey') && !!item.selectionKey;
    };

    const handleBreakdownClick = (item) => {
      if (!canViewDetails(item)) {
        return;
      }
      const id = extractIdFromKey(item.selectionKey);
      if (!id) {
        return;
      }
      if (item.type === 'Horse') {
        emit('viewHorse', id, item.name);
      } else if (item.type === 'Jockey') {
        emit('viewJockey', id, item.name);
      }
    };

    return {
      dailyPointBars,
      bestDailyPointDay,
      selectedDailyPointDay,
      setActiveDailyPointDate,
      formatPoints,
      formatDisplayDate,
      formatDailyPointLabel,
      canViewDetails,
      handleBreakdownClick,
    };
  },
};
</script>

<style scoped>
.daily-points-card {
  margin: 1.25rem 0 1rem;
  padding: 1rem;
  border: 1px solid rgba(100, 255, 218, 0.18);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(2, 12, 27, 0.45), rgba(17, 34, 64, 0.65));
}

.daily-points-card__header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.9rem;
}

.daily-points-card__header h4 {
  color: #e6f1ff;
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
}

.daily-points-card__header p {
  color: #8892b0;
  font-size: 0.82rem;
  line-height: 1.35;
  margin: 0;
}

.daily-points-card__best {
  color: #ffb86c;
  border: 1px solid rgba(255, 184, 108, 0.25);
  background: rgba(255, 184, 108, 0.08);
  border-radius: 999px;
  padding: 0.35rem 0.65rem;
  white-space: nowrap;
  font-size: 0.78rem;
  font-weight: 700;
}

.daily-points-chart {
  display: flex;
  align-items: end;
  gap: 0.35rem;
  min-height: 150px;
  padding: 0.75rem 0.25rem 0.45rem;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 255, 218, 0.35) rgba(100, 255, 218, 0.08);
}

button.daily-points-bar-button {
  appearance: none;
  border: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
  min-width: 34px;
  display: grid;
  grid-template-rows: 110px auto auto;
  justify-items: center;
  gap: 0.25rem;
  color: #a8b2d1;
  cursor: pointer;
  transform: none;
}

button.daily-points-bar-button:hover,
button.daily-points-bar-button:focus,
button.daily-points-bar-button.active {
  color: #64ffda;
  outline: none;
  transform: none;
  box-shadow: none;
}

.daily-points-bar-track {
  width: 18px;
  height: 110px;
  display: flex;
  align-items: end;
  border-radius: 999px;
  background: rgba(100, 255, 218, 0.08);
  overflow: hidden;
  border: 1px solid rgba(100, 255, 218, 0.12);
}

.daily-points-bar {
  width: 100%;
  min-height: 4px;
  border-radius: 999px 999px 0 0;
  background: #64ffda;
  opacity: 0.75;
  transition: height 0.2s ease, opacity 0.2s ease, background-color 0.2s ease;
}

button.daily-points-bar-button.active .daily-points-bar {
  background: #ffb86c;
  opacity: 1;
}

.daily-points-bar-value {
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffb86c;
}

.daily-points-bar-date {
  font-size: 0.72rem;
  color: inherit;
  white-space: nowrap;
}

.daily-points-detail {
  margin-top: 0.85rem;
  border-top: 1px solid rgba(100, 255, 218, 0.12);
  padding-top: 0.85rem;
}

.daily-points-detail__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  align-items: baseline;
  color: #ccd6f6;
  margin-bottom: 0.75rem;
}

.daily-points-detail__summary strong {
  color: #64ffda;
}

.daily-points-detail__summary span {
  color: #ffb86c;
  font-weight: 800;
}

.daily-points-detail__summary small {
  color: #8892b0;
}

.daily-points-detail__groups {
  display: grid;
  gap: 0.75rem;
}

.daily-points-detail__group h5 {
  color: #e6f1ff;
  font-size: 0.85rem;
  font-weight: 700;
  margin: 0 0 0.35rem;
}

button.daily-points-detail__row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  background: rgba(100, 255, 218, 0.05);
  color: #a8b2d1;
  box-shadow: none;
  transform: none;
  font-size: 0.86rem;
}

button.daily-points-detail__row.clickable {
  cursor: pointer;
}

button.daily-points-detail__row.clickable:hover {
  background: rgba(100, 255, 218, 0.1);
  transform: none;
  box-shadow: none;
}

.daily-points-detail__row span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daily-points-detail__row strong {
  color: #64ffda;
  white-space: nowrap;
}

@media (max-width: 480px) {
  .daily-points-card__header {
    flex-direction: column;
  }

  .daily-points-card__best {
    white-space: normal;
    border-radius: 10px;
  }

  .daily-points-chart {
    gap: 0.3rem;
  }

  button.daily-points-bar-button {
    min-width: 30px;
  }
}
</style>
